import { useEffect } from 'react'

const DEFAULT_IMAGE = `${process.env.PUBLIC_URL}/images/provencePaie_logoS.png`

function updateMeta(attrKey, attrValue, content) {
  const selector = attrKey === 'property'
    ? `meta[${attrKey}="${attrValue}"]`
    : `meta[${attrKey}="${attrValue}"]`
  let element = document.head.querySelector(selector)

  if (!element) {
    element = document.createElement('meta')
    element.setAttribute(attrKey, attrValue)
    document.head.appendChild(element)
  }

  element.setAttribute('content', content)
  return element
}

function updateLinkRel(rel, href) {
  let link = document.head.querySelector(`link[rel="${rel}"]`)

  if (!link) {
    link = document.createElement('link')
    link.setAttribute('rel', rel)
    document.head.appendChild(link)
  }

  link.setAttribute('href', href)
  return link
}

export default function Seo({ title, description, pathname = '/', image, keywords }) {
  useEffect(() => {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://provencepaie.com'
    const normalizedPath = pathname.startsWith('/') ? pathname : `/${pathname}`
    const canonicalUrl = new URL(normalizedPath, origin).href
    const pageTitle = title || 'Provence Paie'
    const pageDescription = description || 'Provence Paie accompagne les cabinets d expertise comptable avec une gestion sociale sur mesure.'
    const pageImage = image || DEFAULT_IMAGE

    document.title = pageTitle
    document.documentElement.lang = 'fr'

    updateMeta('name', 'description', pageDescription)
    updateMeta('name', 'robots', 'index, follow')
    updateMeta('property', 'og:title', pageTitle)
    updateMeta('property', 'og:description', pageDescription)
    updateMeta('property', 'og:type', 'website')
    updateMeta('property', 'og:url', canonicalUrl)
    updateMeta('property', 'og:image', pageImage)
    updateMeta('property', 'og:image:alt', 'Provence Paie - expertise paie et RH')
    updateMeta('property', 'og:site_name', 'Provence Paie')
    updateMeta('name', 'twitter:card', 'summary_large_image')
    updateMeta('name', 'twitter:title', pageTitle)
    updateMeta('name', 'twitter:description', pageDescription)
    updateMeta('name', 'twitter:image', pageImage)

    if (keywords) {
      updateMeta('name', 'keywords', keywords)
    }

    updateLinkRel('canonical', canonicalUrl)

    const jsonLd = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Organization',
          'name': 'Provence Paie',
          'url': origin,
          'logo': `${origin}/images/provencePaie_logoS.png`,
          'sameAs': ['https://provence-compta.com'],
          'contactPoint': [
            {
              '@type': 'ContactPoint',
              'telephone': '+33 4 90 18 88 51',
              'contactType': 'customer support',
              'areaServed': 'FR',
              'availableLanguage': ['Français']
            }
          ],
          'address': {
            '@type': 'PostalAddress',
            'streetAddress': 'RESIDENCE LE MONTE CARLO, 355 BOULEVARD LEDRU ROLLIN',
            'addressLocality': 'Salon-de-Provence',
            'postalCode': '13300',
            'addressCountry': 'FR'
          }
        },
        {
          '@type': 'WebSite',
          'url': origin,
          'name': 'Provence Paie',
          'publisher': {
            '@type': 'Organization',
            'name': 'Provence Paie'
          },
          'potentialAction': {
            '@type': 'SearchAction',
            'target': `${origin}/?q={search_term_string}`,
            'query-input': 'required name=search_term_string'
          }
        }
      ]
    }

    let script = document.head.querySelector('script[type="application/ld+json"]')
    if (!script) {
      script = document.createElement('script')
      script.type = 'application/ld+json'
      document.head.appendChild(script)
    }
    script.textContent = JSON.stringify(jsonLd)
  }, [title, description, pathname, image, keywords])

  return null
}
